/**
 * @description
 * Custom hook for managing Anthropic page data and state.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../../services/api";
import type { AnthropicState, TabType } from "../types";

export function useAnthropicData() {
  const [state, setState] = useState<AnthropicState>({
    activeTab: "organization",
    loading: false,
    organization: null,
    workspaces: [],
    clients: [],
    showCreateWorkspace: false,
    newWorkspaceName: "",
    assigningClients: null,
    selectedClients: [],
    apiKeys: [],
    usageData: [],
    usageStartingAt: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      return date.toISOString();
    })(),
    usageEndingAt: new Date().toISOString(),
    usageBucketWidth: "1d",
    costData: [],
    costStartingAt: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      return date.toISOString();
    })(),
    costEndingAt: new Date().toISOString(),
    costBucketWidth: "1d",
  });

  useEffect(() => {
    if (state.activeTab === "organization") {
      fetchOrganization();
      fetchWorkspaces();
      fetchApiKeys();
      fetchUsageForAnalytics();
    } else if (state.activeTab === "workspaces") {
      fetchWorkspaces();
      fetchClients();
    } else if (state.activeTab === "api-keys") {
      fetchApiKeys();
      fetchWorkspaces();
    } else if (state.activeTab === "usage") {
      fetchWorkspaces();
    } else if (state.activeTab === "cost") {
      fetchWorkspaces();
    }
  }, [state.activeTab]);

  const fetchOrganization = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const org = await api.getAnthropicOrganization();
      setState(prev => ({ ...prev, organization: org }));
    } catch (error) {
      console.error("Error fetching organization:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to fetch organization",
      );
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchWorkspaces = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const ws = await api.getAnthropicWorkspaces();
      setState(prev => ({ ...prev, workspaces: ws }));
    } catch (error) {
      console.error("Error fetching workspaces:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to fetch workspaces",
      );
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchClients = async () => {
    try {
      const clientList = await api.getClients();
      setState(prev => ({ ...prev, clients: clientList }));
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchApiKeys = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const keys = await api.getAnthropicApiKeys();
      setState(prev => ({ ...prev, apiKeys: keys }));
    } catch (error) {
      console.error("Error fetching API keys:", error);
      toast.error("Failed to fetch API keys");
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchUsageForAnalytics = async () => {
    try {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      const response = await api.getAnthropicUsage(
        date.toISOString(),
        new Date().toISOString(),
        {
          bucketWidth: "1d",
          groupBy: ["workspace_id", "model"],
        },
      );
      setState(prev => ({ ...prev, usageData: response.data || [] }));
    } catch (error) {
      console.error("Error fetching usage for analytics:", error);
      // Set empty array to prevent undefined errors
      setState(prev => ({ ...prev, usageData: [] }));
      // Only show error if it's not a configuration issue
      if (error instanceof Error && !error.message.includes('not configured')) {
        toast.error("Failed to fetch usage analytics");
      }
    }
  };

  const fetchUsage = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await api.getAnthropicUsage(
        state.usageStartingAt,
        state.usageEndingAt,
        {
          bucketWidth: state.usageBucketWidth,
          groupBy: ["workspace_id", "model"],
        },
      );
      setState(prev => ({ ...prev, usageData: response.data }));
    } catch (error) {
      console.error("Error fetching usage:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to fetch usage data",
      );
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchCost = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await api.getAnthropicCost(
        state.costStartingAt,
        state.costEndingAt,
        {
          bucketWidth: state.costBucketWidth,
          groupBy: ["workspace_id", "description"],
        },
      );
      setState(prev => ({ ...prev, costData: response.data }));
    } catch (error) {
      console.error("Error fetching cost:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to fetch cost data",
      );
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCreateWorkspace = async () => {
    if (!state.newWorkspaceName.trim()) {
      toast.error("Workspace name is required");
      return;
    }

    try {
      await api.createAnthropicWorkspace(state.newWorkspaceName);
      toast.success("Workspace created successfully");
      setState(prev => ({
        ...prev,
        showCreateWorkspace: false,
        newWorkspaceName: "",
      }));
      fetchWorkspaces();
    } catch (error) {
      console.error("Error creating workspace:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to create workspace",
      );
    }
  };

  const handleArchiveWorkspace = async (id: string) => {
    if (!confirm("Are you sure you want to archive this workspace?")) {
      return;
    }

    try {
      await api.archiveAnthropicWorkspace(id);
      toast.success("Workspace archived successfully");
      fetchWorkspaces();
    } catch (error) {
      console.error("Error archiving workspace:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to archive workspace",
      );
    }
  };

  const handleAssignClients = async (workspaceId: string) => {
    try {
      await api.assignClientsToWorkspace(workspaceId, state.selectedClients);
      toast.success("Clients assigned successfully");
      setState(prev => ({
        ...prev,
        assigningClients: null,
        selectedClients: [],
      }));
      fetchWorkspaces();
    } catch (error) {
      console.error("Error assigning clients:", error);
      toast.error("Failed to assign clients");
    }
  };

  const getWorkspaceName = (workspaceId: string | null): string => {
    if (!workspaceId) return "N/A";
    const workspace = state.workspaces.find((w) => w.anthropicId === workspaceId);
    return workspace ? workspace.name : workspaceId;
  };

  const calculateTotalTokens = () => {
    let uncached = 0;
    let cacheCreation1h = 0;
    let cacheCreation5m = 0;
    let cacheRead = 0;
    let output = 0;

    state.usageData.forEach((bucket) => {
      bucket.results.forEach((result) => {
        uncached += result.uncached_input_tokens;
        cacheCreation1h += result.cache_creation.ephemeral_1h_input_tokens;
        cacheCreation5m += result.cache_creation.ephemeral_5m_input_tokens;
        cacheRead += result.cache_read_input_tokens;
        output += result.output_tokens;
      });
    });

    return { uncached, cacheCreation1h, cacheCreation5m, cacheRead, output };
  };

  const getWorkspaceUsageData = () => {
    const workspaceMap = new Map<string, number>();
    state.usageData.forEach((bucket) => {
      bucket.results.forEach((result) => {
        const wsName = getWorkspaceName(result.workspace_id);
        const total =
          result.uncached_input_tokens +
          result.cache_read_input_tokens +
          result.output_tokens;
        workspaceMap.set(wsName, (workspaceMap.get(wsName) || 0) + total);
      });
    });
    return Array.from(workspaceMap.entries()).map(([name, value]) => ({
      name,
      value,
    }));
  };

  const getModelUsageData = () => {
    const modelMap = new Map<string, number>();
    state.usageData.forEach((bucket) => {
      bucket.results.forEach((result) => {
        const model = result.model || "Unknown";
        const total =
          result.uncached_input_tokens +
          result.cache_read_input_tokens +
          result.output_tokens;
        modelMap.set(model, (modelMap.get(model) || 0) + total);
      });
    });
    return Array.from(modelMap.entries()).map(([name, value]) => ({
      name,
      value,
    }));
  };

  const calculateTotalCost = () => {
    let total = 0;
    state.costData.forEach((bucket) => {
      bucket.results.forEach((result) => {
        // Amount is in cents, convert to dollars
        total += parseFloat(result.amount) / 100;
      });
    });
    return total;
  };

  const setActiveTab = (tab: TabType) => {
    setState(prev => ({ ...prev, activeTab: tab }));
  };

  const setShowCreateWorkspace = (show: boolean) => {
    setState(prev => ({ ...prev, showCreateWorkspace: show }));
  };

  const setNewWorkspaceName = (name: string) => {
    setState(prev => ({ ...prev, newWorkspaceName: name }));
  };

  const setAssigningClients = (id: string | null) => {
    setState(prev => ({ ...prev, assigningClients: id }));
  };

  const setSelectedClients = (clients: string[]) => {
    setState(prev => ({ ...prev, selectedClients: clients }));
  };

  const setUsageStartingAt = (date: string) => {
    setState(prev => ({ ...prev, usageStartingAt: date }));
  };

  const setUsageEndingAt = (date: string) => {
    setState(prev => ({ ...prev, usageEndingAt: date }));
  };

  const setUsageBucketWidth = (width: "1d" | "1h" | "1m") => {
    setState(prev => ({ ...prev, usageBucketWidth: width }));
  };

  const setCostStartingAt = (date: string) => {
    setState(prev => ({ ...prev, costStartingAt: date }));
  };

  const setCostEndingAt = (date: string) => {
    setState(prev => ({ ...prev, costEndingAt: date }));
  };

  const setCostBucketWidth = (width: "1d") => {
    setState(prev => ({ ...prev, costBucketWidth: width }));
  };

  return {
    // State
    ...state,
    
    // Actions
    setActiveTab,
    setShowCreateWorkspace,
    setNewWorkspaceName,
    setAssigningClients,
    setSelectedClients,
    setUsageStartingAt,
    setUsageEndingAt,
    setUsageBucketWidth,
    setCostStartingAt,
    setCostEndingAt,
    setCostBucketWidth,
    
    // Data fetching
    fetchOrganization,
    fetchWorkspaces,
    fetchClients,
    fetchApiKeys,
    fetchUsage,
    fetchCost,
    
    // Actions
    handleCreateWorkspace,
    handleArchiveWorkspace,
    handleAssignClients,
    
    // Utilities
    getWorkspaceName,
    calculateTotalTokens,
    getWorkspaceUsageData,
    getModelUsageData,
    calculateTotalCost,
  };
}
